import { useCallback, useMemo, useRef, useState } from "react";
import { useMountEffect } from "primereact/hooks";
import UserService from "../../../../services/UserService";
import {
  PICKER_PAGE_SIZE,
  useServerSearch,
  withSelected,
} from "../../../common/hooks/useServerSearch";

const label = (firstName, lastName, email) =>
  `${[firstName, lastName].filter(Boolean).join(" ")} (${email})`;

// The user details manager picker (E2E26): the first page of users by first
// name, then the server's matches (name or email) as the admin types, without
// the user being edited, and always with the current manager (from the user
// details) so it shows even when the results leave it out.
//
// The candidates are kept here, not in usersSlice: its list request clears
// state.users.record, which is the user this page shows.
export const useManagerPicker = (record) => {
  const [candidates, setCandidates] = useState([]);
  const latestRequest = useRef(0);

  const load = useCallback(async (namePattern = "") => {
    const request = ++latestRequest.current;
    try {
      const ajax = await UserService.list({
        query: {
          pageNo: 1,
          pageSize: PICKER_PAGE_SIZE,
          sortBy: "asc",
          sortColumn: "firstName",
          namePattern,
        },
        filters: [],
      });
      // A slower, older answer must not replace the newer one
      if (request === latestRequest.current && ajax?.status === 200) {
        setCandidates(ajax.data?.response?.list || []);
      }
    } catch (error) {
      if (request === latestRequest.current) {
        setCandidates([]);
      }
    }
  }, []);

  const onFilter = useServerSearch(load);

  useMountEffect(() => {
    load();
  });

  const options = useMemo(
    () =>
      withSelected(
        candidates
          .filter((user) => user?.id !== record?.id)
          .map((user) => ({
            name: label(user?.firstName, user?.lastName, user?.email),
            value: user?.id,
            id: user?.id,
          })),
        record?.managerId && {
          name: label(record.managerName, "", record.managerEmail),
          value: record.managerId,
          id: record.managerId,
        }
      ),
    [candidates, record?.id, record?.managerId, record?.managerName, record?.managerEmail]
  );

  return { options, onFilter, load };
};
